import logoBeOneIcon from "@/assets/b1-logo-icon.svg";
import logoBeOneAgency from "@/assets/logo.svg";
import { useT } from "../i18n";

interface WelcomeScreenProps {
  totalQuestions: number;
  onStart: () => void;
}

export function WelcomeScreen({ totalQuestions, onStart }: WelcomeScreenProps) {
  const t = useT();
  return (
    <div
      className="min-h-screen flex items-center justify-center p-0 md:p-6"
      style={{
        background: "linear-gradient(to bottom right, #00AFFD, #02324A)",
      }}
    >
      <div className="flex flex-col md:flex-row gap-0 max-w-6xl w-full items-stretch">
        {/* Left: image with overlay (title + button). On mobile: logo also appears here */}
        <div className="relative w-full md:w-1/2 h-[425px] md:h-auto">
          <picture>
            <source
              srcSet="/business-meeting-640.avif 640w, /business-meeting-960.avif 960w, /business-meeting.avif 1344w"
              sizes="(min-width: 768px) 50vw, 100vw"
              type="image/avif"
            />
            <source
              srcSet="/business-meeting-640.webp 640w, /business-meeting-960.webp 960w, /business-meeting.webp 1344w"
              sizes="(min-width: 768px) 50vw, 100vw"
              type="image/webp"
            />
            <img
              src="/business-meeting.jpg"
              alt="Business meeting"
              width={1344}
              height={1536}
              fetchPriority="high"
              className="rounded-none md:rounded-l-2xl shadow-xl object-cover w-full h-full"
            />
          </picture>
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-6">
            {/* B1 icon logo — mobile only, inside image */}
            <img
              src={logoBeOneIcon}
              alt="Be-one logo"
              width={50}
              height={50}
              className="h-[50px] mx-auto block md:hidden brightness-0 invert"
            />
            <h1 className="font-[Roboto_Slab] text-white text-center leading-tight px-8 text-[28px] md:text-[48px] mt-[40px] md:mt-0">
              {t("welcome.title")}
            </h1>
            <div className="flex flex-col items-center gap-2 mt-[20px]">
              <button
                type="button"
                onClick={onStart}
                className="text-white py-4 px-8 rounded-lg text-lg font-medium transition-colors font-[Roboto]"
                style={{ backgroundColor: "#FF00D2" }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#cc00a8")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "#FF00D2")
                }
              >
                {t("welcome.cta")}
              </button>
              <p className="text-white/70 text-sm font-[Roboto] italic text-center md:mt-4">
                {t("welcome.tagline")}
              </p>
            </div>
          </div>
        </div>

        {/* Right: white panel. On desktop: logo on top. On mobile: logo hidden (it's in the image) */}
        <div className="bg-white shadow-xl w-full md:w-1/2 text-center px-[20px] py-[48px] rounded-none md:rounded-r-2xl">
          <img
            src={logoBeOneAgency}
            alt="Be-one Marketeers' Agency"
            width={128}
            height={56}
            className="h-11 md:h-14 mx-auto mb-[41px] hidden md:block"
          />
          <p className="text-gray-600 mb-8 font-[Roboto] text-left px-0 md:px-[32px] text-[16px]">
            <span className="font-bold">{t("welcome.intro.bold")}</span>
            <br />
            <br />
            {t("welcome.intro.p1")}
            <br />
            <br />
            {t("welcome.intro.p2")}
            <br />
            <br />
            {t("welcome.intro.p3")}
          </p>
          <div className="bg-blue-50 rounded-lg mb-8 text-left px-[24px] py-[24px] mx-0 md:mx-[32px]">
            <h2 className="mb-3 font-[Roboto_Slab] text-[#ff00d2]">
              {t("welcome.summary.title")}
            </h2>
            <ul className="space-y-2 text-gray-700">
              <li className="hidden">
                <span
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ backgroundColor: "#00AFFD" }}
                ></span>
                {t("welcome.summary.li1", { n: totalQuestions })}
              </li>
              <li className="flex items-center gap-2 font-[Roboto]">
                <span
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ backgroundColor: "#00AFFD" }}
                ></span>
                {t("welcome.summary.li2")}
              </li>
              <li className="flex items-center gap-2 font-[Roboto]">
                <span
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ backgroundColor: "#00AFFD" }}
                ></span>
                {t("welcome.summary.li3")}
              </li>
            </ul>
          </div>
          <img
            src={logoBeOneAgency}
            alt="Be-one Marketeers' Agency"
            width={101}
            height={44}
            className="h-11 mx-auto block md:hidden"
          />
        </div>
      </div>
    </div>
  );
}
